import { useState } from "react";
import { toast } from "sonner";
import { Lock, Save, Loader2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [goal, setGoal] = useState("");
  const [raised, setRaised] = useState("");
  const [donors, setDonors] = useState("");
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(false);

  const verify = async () => {
    if (!passcode) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/verify`, { passcode });
      setGoal(String(data.goal));
      setRaised(String(data.raised));
      setDonors(String(data.donors_count));
      setUnlocked(true);
      toast.success("Unlocked");
      try {
        const g = await axios.get(`${API}/admin/gifts`, { params: { passcode } });
        setGifts(g.data.gifts || []);
      } catch (e) {
        /* ignore */
      }
    } catch (e) {
      toast.error("Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/settings`, {
        passcode,
        goal: parseFloat(goal),
        raised: parseFloat(raised),
        donors_count: parseInt(donors, 10),
      });
      setGoal(String(data.goal));
      setRaised(String(data.raised));
      setDonors(String(data.donors_count));
      toast.success("Saved. The page is updated.");
    } catch (e) {
      toast.error("Could not save. Check your passcode.");
    } finally {
      setLoading(false);
    }
  };

  const STATUS_CYCLE = { pending: "confirmed", confirmed: "sent", sent: "pending" };
  const STATUS_STYLE = {
    pending: "bg-sand text-ink/60 border-border-soft",
    confirmed: "bg-olive/15 text-olive border-olive/40",
    sent: "bg-terracotta text-sand border-terracotta",
  };

  const cycleStatus = async (g) => {
    const next = STATUS_CYCLE[g.status || "pending"];
    setGifts((prev) => prev.map((x) => (x.id === g.id ? { ...x, status: next } : x)));
    try {
      await axios.post(`${API}/admin/gifts/status`, { passcode, id: g.id, status: next });
      toast.success(`Marked ${next}`);
    } catch (e) {
      toast.error("Could not update status.");
      setGifts((prev) => prev.map((x) => (x.id === g.id ? { ...x, status: g.status } : x)));
    }
  };

  const field = "w-full border border-border-soft bg-white/60 px-4 py-3 text-lg text-ink outline-none transition-colors focus:border-terracotta";

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-lg border border-border-soft bg-sand-2 p-8" data-testid="admin-panel">
        <Link
          to="/"
          data-testid="admin-back-link"
          className="inline-flex items-center gap-2 text-sm text-ink/50 hover:text-ink transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back to the page
        </Link>

        <div className="mt-6 flex items-center gap-2 text-terracotta">
          <Lock className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Private admin</span>
        </div>
        <h1 className="mt-3 font-serif text-3xl text-ink">Update the totals</h1>

        {!unlocked ? (
          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.16em] text-ink/50">Passcode</label>
            <input
              data-testid="admin-passcode-input"
              type="password"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && verify()}
              placeholder="Enter passcode"
              className={`mt-1 ${field}`}
            />
            <button
              data-testid="admin-unlock-button"
              onClick={verify}
              disabled={loading}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3.5 text-sm font-semibold text-sand transition-colors hover:bg-terracotta disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Unlock
            </button>
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-ink/50">
                Amount raised (USD)
              </label>
              <input
                data-testid="admin-raised-input"
                type="number"
                value={raised}
                onChange={(e) => setRaised(e.target.value)}
                className={`mt-1 ${field}`}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-ink/50">Goal (USD)</label>
              <input
                data-testid="admin-goal-input"
                type="number"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className={`mt-1 ${field}`}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-ink/50">
                Number of donations
              </label>
              <input
                data-testid="admin-donors-input"
                type="number"
                value={donors}
                onChange={(e) => setDonors(e.target.value)}
                className={`mt-1 ${field}`}
              />
            </div>
            <button
              data-testid="admin-save-button"
              onClick={save}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-terracotta py-3.5 text-sm font-semibold text-sand transition-colors hover:bg-terracotta-hover disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>

            <div className="mt-8 border-t border-border-soft pt-6" data-testid="admin-gifts">
              <div className="text-xs uppercase tracking-[0.16em] text-ink/50">
                Thank-you gift requests ({gifts.length})
              </div>
              <p className="mt-1 text-xs text-ink/40">
                Match the reference against your bank statement, then tap the badge to move it
                Pending → Confirmed → Sent.
              </p>
              <div className="mt-3 max-h-80 overflow-y-auto space-y-3">
                {gifts.length === 0 && <p className="text-sm text-ink/40">No requests yet.</p>}
                {gifts.map((g) => (
                  <div key={g.id} className="border border-border-soft p-3 text-sm" data-testid={`admin-gift-${g.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ink">{g.name}</span>
                      <button
                        data-testid={`admin-gift-status-${g.id}`}
                        onClick={() => cycleStatus(g)}
                        className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${STATUS_STYLE[g.status || "pending"]}`}
                      >
                        {g.status || "pending"}
                      </button>
                    </div>
                    <div className="mt-1 text-terracotta uppercase text-xs">
                      {g.gift}{g.size ? ` · ${g.size}` : ""}
                    </div>
                    <div className="text-ink/60">{g.country}{g.email ? ` · ${g.email}` : ""}</div>
                    <div className="text-ink/60 whitespace-pre-wrap">{g.address}</div>
                    {(g.reference || g.amount) && (
                      <div className="mt-1.5 rounded bg-sand-2 px-2 py-1 text-xs text-ink/70">
                        {g.reference ? <span>Ref: <b>{g.reference}</b></span> : null}
                        {g.reference && g.amount ? " · " : ""}
                        {g.amount ? <span>Amount: <b>{g.amount}</b></span> : null}
                      </div>
                    )}
                    {g.message ? <div className="mt-1 text-ink/40 italic">{g.message}</div> : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
